/**
 * /tracking/flutter-sdk — Flutter SDK config + installations page (feature #185).
 *
 * Declares a TanStack Router route and renders the `FlutterSdkPage` feature.
 * The component is picked up by the root-owned `router.tsx` module
 * augmentation during the wire-up cycle — agents never touch `router.tsx`.
 *
 * Deep-link search params:
 *   ?appId=fla_...   — pre-select an app in the installations filter.
 *   ?platform=ios|android — pre-apply a platform chip filter.
 * Both are validated with the same guards shared by the feature module so
 * malformed URLs are silently dropped rather than throwing.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { FlutterSdkPage } from "@/features/flutter-sdk/FlutterSdkPage";
import {
	isFlutterPlatform,
	isValidAppId,
	type FlutterPlatform,
} from "@/features/flutter-sdk/api";

interface TrackingFlutterSdkSearch {
	appId?: string;
	platform?: FlutterPlatform;
}

export const trackingFlutterSdkRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/flutter-sdk",
	validateSearch: (raw: Record<string, unknown>): TrackingFlutterSdkSearch => {
		const out: TrackingFlutterSdkSearch = {};
		const appId = raw.appId;
		if (typeof appId === "string" && isValidAppId(appId)) {
			out.appId = appId;
		}
		const platform = raw.platform;
		if (typeof platform === "string" && isFlutterPlatform(platform)) {
			out.platform = platform;
		}
		return out;
	},
	component: TrackingFlutterSdkRoute,
});

export default trackingFlutterSdkRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + the live feature component.
// ---------------------------------------------------------------------------

export function TrackingFlutterSdkRoute() {
	const { appId, platform } = trackingFlutterSdkRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<FlutterSdkPage initialAppId={appId} initialPlatform={platform} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Flutter SDK</li>
			</ol>
		</nav>
	);
}

// Named re-export for the root wire-up cycle.
export { TrackingFlutterSdkRoute as TrackingFlutterSdkPage };
